import {Component, DoCheck, OnInit} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {EMPTY} from 'rxjs';
import {AutoScraperBackendService} from '../auto-scraper-backend.service';
import {environment} from '../../environments/environment';
import {DirectHitSelectors, FallbackValues, SelectorResult, StaticValues, SupplierImplementation} from '../model';
import {chainOrder, moveStep, newSchemaStep, stepKind} from '../extraction-steps';

interface SelectorSection {
  label: string;
  anchor: string;
  /** Field path as AutoScraper names it, used for the default schema lookup. */
  field: string;
  getList: () => SelectorResult[];
  /** Key of the field's static and fallback value; fields without one have neither. */
  staticValueKey?: keyof StaticValues;
}

@Component({
    selector: 'app-direct-hit-selectors-manager',
    templateUrl: './direct-hit-selectors-manager.component.html',
    styleUrls: ['./direct-hit-selectors-manager.component.css'],
    standalone: false
})
export class DirectHitSelectorsManagerComponent implements OnInit, DoCheck {
  sections: SelectorSection[] = [];
  private lastImpl: SupplierImplementation | null = null;
  private expandedPaths = new WeakSet<SelectorResult>();
  private expandedPP = new WeakSet<SelectorResult>();
  readonly collapsedPathLimit = 5;
  htmlInput = '';
  testStatus = '';
  private testResults = new WeakMap<SelectorResult, { extracted_value: string; error: string | null }>();

  constructor(private http: HttpClient, public data: AutoScraperBackendService) {
  }

  /** Initializes section definitions from the current supplier implementation. */
  ngOnInit(): void {
    this.buildSections();
  }

  /** Rebuilds sections when the supplier_implementation reference changes (detected on every change-detection cycle). */
  ngDoCheck(): void {
    if (this.data.supplier_implementation !== this.lastImpl) {
      this.buildSections();
    }
  }

  /** Constructs the ordered list of SelectorSection objects from the current implementation. */
  private buildSections(): void {
    const impl = this.data.supplier_implementation;
    this.lastImpl = impl;
    if (!impl) {
      this.sections = [];
      return;
    }
    const dhs = this.ensureFields(impl);
    this.sections = [
      {label: 'OEM Info → Brand', anchor: 'sec-oem_info-brand', field: 'oem_info.brand', getList: () => dhs.oem_info.brand, staticValueKey: 'oem_info_brand'},
      {label: 'OEM Info → Number', anchor: 'sec-oem_info-number', field: 'oem_info.number', getList: () => dhs.oem_info.number, staticValueKey: 'oem_info_number'},
      {label: 'OEM Info → GTIN', anchor: 'sec-oem_info-gtin', field: 'oem_info.gtin', getList: () => dhs.oem_info.gtin, staticValueKey: 'oem_info_gtin'},
      {label: 'Internal Info → Name', anchor: 'sec-internal_info-name', field: 'internal_info.name', getList: () => dhs.internal_info.name, staticValueKey: 'internal_info_name'},
      {label: 'Internal Info → Number', anchor: 'sec-internal_info-number', field: 'internal_info.number', getList: () => dhs.internal_info.number, staticValueKey: 'internal_info_number'},
      {label: 'Internal Info → Description', anchor: 'sec-internal_info-description', field: 'internal_info.description', getList: () => dhs.internal_info.description, staticValueKey: 'internal_info_description'},
      {label: 'Internal Info → Group', anchor: 'sec-internal_info-group', field: 'internal_info.group', getList: () => dhs.internal_info.group, staticValueKey: 'internal_info_group'},
      {label: 'Prices → Amount', anchor: 'sec-prices-amount', field: 'prices.amount', getList: () => dhs.prices.amount},
      {label: 'Prices → Price', anchor: 'sec-prices-price', field: 'prices.price', getList: () => dhs.prices.price, staticValueKey: 'prices_price'},
      {label: 'Prices → Delivery Text', anchor: 'sec-prices-delivery_text', field: 'prices.delivery_text', getList: () => dhs.prices.delivery_text, staticValueKey: 'prices_delivery_text'},
      {label: 'Prices → Currency Text', anchor: 'sec-prices-currency_text', field: 'prices.currency_text', getList: () => dhs.prices.currency_text, staticValueKey: 'prices_currency_text'},
      {label: 'Prices → VAT Text', anchor: 'sec-prices-vat_text', field: 'prices.vat_text', getList: () => dhs.prices.vat_text, staticValueKey: 'prices_vat_text'},
      {label: 'Condition', anchor: 'sec-condition', field: 'condition', getList: () => dhs.condition, staticValueKey: 'condition'},
      {label: 'Price status (login / request / used / protected)', anchor: 'sec-price-status', field: 'price_status', getList: () => dhs.price_status},
    ];
  }

  /** Guarantees direct_hits_selectors and all sub-arrays are present; initializes missing fields to empty defaults. */
  private ensureFields(impl: SupplierImplementation): DirectHitSelectors {
    if (!impl.direct_hits_selectors) {
      impl.direct_hits_selectors = {
        internal_info: {number: [], name: [], description: [], group: [], supplier: []},
        oem_info: {number: [], part_type: [], gtin: [], brand: []},
        condition: [],
        price_status: [],
        prices: {amount: [], price: [], delivery: [], delivery_text: [], currency_text: [], vat_text: []},
        static_values: null,
      };
    }
    const dhs = impl.direct_hits_selectors;
    if (!dhs.internal_info) {
      dhs.internal_info = {number: [], name: [], description: [], group: [], supplier: []};
    }
    if (!dhs.oem_info) {
      dhs.oem_info = {number: [], part_type: [], gtin: [], brand: []};
    }
    if (!dhs.prices) {
      dhs.prices = {amount: [], price: [], delivery: [], delivery_text: [], currency_text: [], vat_text: []};
    }
    dhs.internal_info.number = dhs.internal_info.number ?? [];
    dhs.internal_info.name = dhs.internal_info.name ?? [];
    dhs.internal_info.description = dhs.internal_info.description ?? [];
    dhs.internal_info.group = dhs.internal_info.group ?? [];
    dhs.internal_info.supplier = dhs.internal_info.supplier ?? [];
    dhs.oem_info.number = dhs.oem_info.number ?? [];
    dhs.oem_info.part_type = dhs.oem_info.part_type ?? [];
    dhs.oem_info.gtin = dhs.oem_info.gtin ?? [];
    dhs.oem_info.brand = dhs.oem_info.brand ?? [];
    dhs.condition = dhs.condition ?? [];
    dhs.price_status = dhs.price_status ?? [];
    dhs.prices.amount = dhs.prices.amount ?? [];
    dhs.prices.price = dhs.prices.price ?? [];
    dhs.prices.delivery = dhs.prices.delivery ?? [];
    dhs.prices.delivery_text = dhs.prices.delivery_text ?? [];
    dhs.prices.currency_text = dhs.prices.currency_text ?? [];
    dhs.prices.vat_text = dhs.prices.vat_text ?? [];
    return dhs;
  }

  // ─── Extraction chain ─────────────────────────────────────────────────────────

  /** Returns the section's steps in the order extraction tries them. */
  ordered(section: SelectorSection): SelectorResult[] {
    return chainOrder(section.getList());
  }

  /** Returns 'Schema', 'XPath' or 'CSS'. */
  kind(sel: SelectorResult): string {
    return stepKind(sel);
  }

  /** Moves a step one position up (-1) or down (+1) in its chain; rewrites the manual priorities to match. */
  move(section: SelectorSection, sel: SelectorResult, direction: -1 | 1): void {
    moveStep(section.getList(), sel, direction);
  }

  /** Adds a schema step as the first step of the section's chain. */
  addSchemaStep(section: SelectorSection): void {
    section.getList().push(newSchemaStep(section.getList()));
  }

  /** Returns true when a step is skipped by extraction. */
  isDisabled(sel: SelectorResult): boolean {
    return sel.enabled === false;
  }

  /** Enables or disables a step. */
  setEnabled(sel: SelectorResult, enabled: boolean): void {
    sel.enabled = enabled;
  }

  /** Returns the schema key paths as editable text: paths separated by ',', keys by '.' (e.g. 'offers.price, price'). */
  schemaPathsText(sel: SelectorResult): string {
    return (sel.schema_paths ?? []).map(p => p.join('.')).join(', ');
  }

  /** Parses the text form of schema_paths back; an empty text means the field's default lookup. */
  onSchemaPathsChange(sel: SelectorResult, text: string): void {
    sel.schema_paths = (text ?? '')
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0)
      .map(p => p.split('.').map(k => k.trim()).filter(k => k.length > 0));
  }

  /** Returns the static value of a section, or null. */
  staticValue(section: SelectorSection): string | null {
    const key = section.staticValueKey;
    return key ? this.data.supplier_implementation.direct_hits_selectors.static_values?.[key] ?? null : null;
  }

  /** Returns fallback_values, initializing it when the implementation has none yet. */
  get fallbackValues(): FallbackValues {
    const dhs = this.data.supplier_implementation.direct_hits_selectors;
    if (!dhs.fallback_values) {
      dhs.fallback_values = {
        oem_info_brand: null, oem_info_number: null, oem_info_gtin: null,
        internal_info_number: null, internal_info_name: null, internal_info_description: null,
        internal_info_group: null, prices_price: null, prices_delivery_text: null, prices_currency_text: null,
        prices_vat_text: null, condition: null,
      };
    }
    return dhs.fallback_values;
  }

  /** Stores a fallback value; an empty input clears it. */
  setFallbackValue(section: SelectorSection, value: string): void {
    if (section.staticValueKey) {
      this.fallbackValues[section.staticValueKey] = value?.trim() ? value : null;
    }
  }

  /** Returns the paths to display, limited to collapsedPathLimit unless the selector has been expanded. */
  visiblePaths(sel: SelectorResult): string[] {
    const paths = sel.selector ?? [];
    if (this.expandedPaths.has(sel)) {
      return paths;
    }
    return paths.slice(0, this.collapsedPathLimit);
  }

  /** Returns true if the selector's path list is currently expanded. */
  isPathsExpanded(sel: SelectorResult): boolean {
    return this.expandedPaths.has(sel);
  }

  /** Toggles the expanded/collapsed state of a selector's path list. */
  togglePathsExpanded(sel: SelectorResult): void {
    if (this.expandedPaths.has(sel)) {
      this.expandedPaths.delete(sel);
    } else {
      this.expandedPaths.add(sel);
    }
  }

  /** Smoothly scrolls the page to the element with the given id. */
  scrollToAnchor(anchor: string): void {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  /** Returns the total number of selectors across all sections. */
  totalCount(): number {
    let total = 0;
    for (const s of this.sections) {
      total += s.getList().length;
    }
    return total;
  }

  /** Coerces raw to an integer in [-1000, 1000], defaulting non-numeric input to 0, then assigns it as the selector's manual priority. */
  onPriorityChange(sel: SelectorResult, raw: number | string | null): void {
    let n = Number(raw);
    if (!isFinite(n)) {
      n = 0;
    }
    n = Math.trunc(n);
    if (n < -1000) {
      n = -1000;
    }
    if (n > 1000) {
      n = 1000;
    }
    sel.manual_priority = n;
  }

  /** Toggles the inline post-processing editor open or closed for the given selector. */
  togglePP(sel: SelectorResult): void {
    if (this.expandedPP.has(sel)) {
      this.expandedPP.delete(sel);
    } else {
      this.expandedPP.add(sel);
    }
  }

  /** Returns true if the post-processing editor is currently open for the given selector. */
  isPPExpanded(sel: SelectorResult): boolean {
    return this.expandedPP.has(sel);
  }

  /** Returns the number of post-processing steps configured on the given selector. */
  ppStepCount(sel: SelectorResult): number {
    return sel.post_processing?.length ?? 0;
  }

  /** Prompts for confirmation, then removes the step from its section list. */
  deleteSelector(section: SelectorSection, sel: SelectorResult): void {
    if (!confirm(sel.is_schema ? 'Delete this schema step?' : 'Delete this selector?')) {
      return;
    }
    const list = section.getList();
    list.splice(list.indexOf(sel), 1);
  }

  /** Prompts for confirmation, then clears all selectors in the given section. No-op when the section is already empty. */
  deleteAllInSection(section: SelectorSection): void {
    const list = section.getList();
    if (list.length === 0) {
      return;
    }
    if (!confirm(`Delete all ${list.length} selectors in "${section.label}"?`)) {
      return;
    }
    list.length = 0;
  }

  /** Prompts for confirmation, then clears all selectors across every section. No-op when no selectors exist. */
  deleteAllSelectors(): void {
    const total = this.totalCount();
    if (total === 0) {
      return;
    }
    if (!confirm(`Delete ALL ${total} selectors across every field?`)) {
      return;
    }
    for (const s of this.sections) {
      s.getList().length = 0;
    }
  }

  /** Returns the test result for a selector from the last runTest call, or undefined if it has not been tested yet. */
  getTestResult(sel: SelectorResult): { extracted_value: string; error: string | null } | undefined {
    return this.testResults.get(sel);
  }

  /** POSTs all current selectors and the pasted HTML to /test_selectors_on_html, then maps each result back to its selector. */
  runTest(): void {
    if (!this.htmlInput || this.htmlInput.trim().length === 0) {
      this.testStatus = 'Paste HTML first.';
      return;
    }
    const targets: SelectorResult[] = [];
    const payload: object[] = [];
    for (const s of this.sections) {
      for (const sel of s.getList()) {
        targets.push(sel);
        // with post-processing and schema steps, so the test shows what extraction would get from each step
        payload.push({
          selector: sel.selector,
          is_xpath: sel.is_xpath,
          value_to_extract: sel.value_to_extract,
          is_schema: sel.is_schema ?? false,
          schema_paths: sel.schema_paths ?? [],
          field: s.field,
          post_processing: sel.post_processing ?? [],
        });
      }
    }
    if (targets.length === 0) {
      this.testStatus = 'No selectors to test.';
      return;
    }
    this.testStatus = `Testing ${targets.length} selectors...`;
    const headers = new HttpHeaders({'auth': this.data.token});
    this.http.post(environment.apiUrl + '/test_selectors_on_html', {
      page_source: this.htmlInput,
      selectors: payload
    }, {headers})
      .pipe(
        catchError(() => {
          this.testStatus = 'Error';
          return EMPTY;
        })
      )
      .subscribe((res) => {
        if (typeof res === 'string') {
          this.testStatus = res;
          return;
        }
        const body = res as { results: { extracted_value: string; error: string | null }[] };
        if (!body || !Array.isArray(body.results) || body.results.length !== targets.length) {
          this.testStatus = 'Malformed response.';
          return;
        }
        this.testResults = new WeakMap();
        body.results.forEach((r, i) => this.testResults.set(targets[i], r));
        this.testStatus = `Tested ${targets.length} selectors.`;
      });
  }

  /** Discards all stored test results and resets testStatus to an empty string. */
  clearTestResults(): void {
    this.testResults = new WeakMap();
    this.testStatus = '';
  }

  /** POSTs the current implementation to /save_implementation and writes the outcome to the #status DOM element. */
  saveImplementation(): void {
    if (this.data.supplier_implementation.basic_supplier_info.name == null) {
      let status_element = document.getElementById('status');
      // @ts-ignore
      status_element.innerText = 'error. empty supplier name.';
      return;
    }
    const headers = new HttpHeaders({
      'auth': this.data.token
    });

    this.http.post(environment.apiUrl + '/save_implementation', this.data.supplier_implementation, {headers})
      .pipe(
        catchError((error) => {
          let status_element = document.getElementById('status');
          // @ts-ignore
          status_element.innerText = 'Error';
          return EMPTY;
        })
      )
      .subscribe((res) => {
        let status_element = document.getElementById('status');
        if (res === 'Unauthorized.') {
          // @ts-ignore
          status_element.innerText = 'Unauthorized.';
          return;
        }
        if (typeof (res) === 'string') {
          // @ts-ignore
          status_element.innerText = res;
          return;
        }
        this.data.supplier_implementation = res as SupplierImplementation;
        this.buildSections();
        // @ts-ignore
        status_element.innerText = 'saved';
      });
  }
}
